import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Nav } from './components/Nav'
import { WorkRail } from './components/WorkRail'
import { ProcessDial } from './components/ProcessDial'
import { Pricing } from './components/Pricing'
import { useReducedMotion } from './hooks/useReducedMotion'

gsap.registerPlugin(ScrollTrigger)

const HeroScene = lazy(() => import('./components/HeroScene').then((module) => ({ default: module.HeroScene })))
const ReelStage = lazy(() => import('./components/ReelStage').then((module) => ({ default: module.ReelStage })))

function Site() {
  const root = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (reduced) return
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true })
    lenis.on('scroll', ScrollTrigger.update)
    const update = (time: number) => lenis.raf(time * 1000)
    gsap.ticker.add(update)
    gsap.ticker.lagSmoothing(0)
    return () => {
      gsap.ticker.remove(update)
      lenis.destroy()
    }
  }, [reduced])

  useLayoutEffect(() => {
    if (reduced || !root.current) return
    const context = gsap.context(() => {
      gsap.from('.hero-title .hero-line__inner', { yPercent: 112, duration: 1.05, stagger: 0.11, ease: 'power4.out', delay: 0.12 })
      gsap.from('.hero-subtitle, .hero-actions, .hero-index', { y: 30, autoAlpha: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out', delay: 0.7 })
      gsap.utils.toArray<HTMLElement>('.pricing-band').forEach((band, index) => {
        gsap.from(band, { xPercent: index % 2 === 0 ? -14 : 14, autoAlpha: 0, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: band, start: 'top 85%' } })
      })
      gsap.from('.final-cta__copy > *, .door-mark', { y: 55, autoAlpha: 0, stagger: 0.08, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: '.final-cta', start: 'top 72%' } })
    }, root)
    return () => context.revert()
  }, [reduced])

  useEffect(() => {
    document.fonts.ready.then(() => {
      window.requestAnimationFrame(() => {
        window.__Q1_READY__ = true
        ScrollTrigger.refresh()
      })
    })
  }, [])

  return (
    <div className="site" id="top" ref={root}>
      <Nav />
      <main>
        <section className="hero-section">
          <Suspense fallback={<div className="hero-canvas hero-canvas--loading" aria-hidden="true" />}>
            <HeroScene />
          </Suspense>
          <div className="hero-grain" aria-hidden="true" />
          <div className="hero-content">
            <p className="hero-index">INDEPENDENT WEB DESIGN / 2026</p>
            <h1 className="hero-title">
              <span className="hero-line"><span className="hero-line__inner">WEBSITES</span></span>
              <span className="hero-line"><span className="hero-line__inner hero-line__inner--blue">THAT MOVE</span></span>
              <span className="hero-line"><span className="hero-line__inner">BUSINESS.</span></span>
            </h1>
            <p className="hero-subtitle">Distinctive, mobile-first websites<br />for local businesses.</p>
            <div className="hero-actions"><a href="#work">SEE THE WORK <span>↓</span></a><a href="#contact">START A PROJECT <span>↗</span></a></div>
          </div>
          <div className="hero-scroll">SCROLL TO ENTER <i /></div>
        </section>
        <WorkRail />
        <ProcessDial />
        <Pricing />
      </main>
    </div>
  )
}

export default function App() {
  const params = new URLSearchParams(window.location.search)
  const filmMode = params.get('reel') === '1'
  return filmMode ? <Suspense fallback={<div className="app-loading">Q1 / LOADING MOTION</div>}><ReelStage /></Suspense> : <Site />
}
